import { SeoService, SiteContentService } from '@core/services';
import { mergeSiteContent } from '@core/services/site-content/site-content.service';
import { SiteContent } from '@core/services/site-content/site-content.types';
import { Component, DefineDependencies } from '@rhtml/component';
import { Container, Inject } from '@rxdi/core';
import { css, html, LitElement } from '@rxdi/lit-html';
import { ScrollToService } from '@shared/services';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { ContactComponent } from './contact/contact.component';
import { HeroComponent } from './hero/hero.component';
import { JournalComponent } from './journal/journal.component';
import { OpenSourceComponent } from './open-source/open-source.component';
import { ProcessComponent } from './process/process.component';
import { ServicesComponent } from './services/services.component';
import { StackComponent } from './stack/stack.component';
import { WorkComponent } from './work/work.component';

/**
 * The home page. Loads the editable site content once (Style A) and hands
 * each section its slice; the sections are pure UI. If the API is down the
 * built-in defaults render, so the page never breaks.
 */
@Component({
  Settings: {
    selector: 'home-component',
    components: [
      HeroComponent,
      WorkComponent,
      StackComponent,
      ServicesComponent,
      ProcessComponent,
      OpenSourceComponent,
      JournalComponent,
      ContactComponent,
    ],
    styles: [
      css`
        :host {
          display: block;
          min-height: 100vh;
          background: var(--rs-ink);
          color: var(--rs-text);
        }
      `,
    ],
  },
  Providers: DefineDependencies(SiteContentService, SeoService)(Container),
  Loading: () => html``,
  State: function (this: HomeComponent, [siteContent, seo]) {
    return siteContent.content().pipe(
      catchError(() => of(mergeSiteContent([]))),
      tap((c: SiteContent) => seo.updateMeta({ title: c.seo.title, description: c.seo.description }))
    );
  },
  Render: () =>
    function (this: HomeComponent, c: SiteContent) {
      return html`
        <rs-hero .content=${c.hero}></rs-hero>
        <rs-work .content=${c.work}></rs-work>
        <rs-stack .content=${c.stack}></rs-stack>
        <rs-services .content=${c.services}></rs-services>
        <rs-process .content=${c.process}></rs-process>
        <rs-open-source .content=${c.openSource}></rs-open-source>
        <rs-journal></rs-journal>
        <rs-contact .content=${c.contact}></rs-contact>
      `;
    },
})
export class HomeComponent extends LitElement {
  @Inject(ScrollToService)
  private scrollToService: ScrollToService;

  /** The navbar's section links look sections up in this shadow root. */
  OnUpdateFirst() {
    this.scrollToService.setRoot(this.shadowRoot);
  }
}
